import {
  type CalendarDate,
  type SojournDetailDto,
  addDays,
  compareDates,
} from '@room-mates/shared';
import { type ReactElement, useEffect, useState } from 'react';
import { formatDate } from '../../shared/format.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Badge, Button, Dialog, ErrorAlert, LoadingBlock } from '../../shared/ui/index.js';
import {
  groupByHouseAndRoom,
  type NamedPlan,
  occupantsByBed,
  unassignedGuests,
} from '../calendar/calendarOccupancy.js';
import { useAllBeds } from '../houses/houseHooks.js';
import { usePlan } from './sojournHooks.js';

export interface DayByDayRecapProps {
  readonly sojourn: SojournDetailDto;
  readonly onClose: () => void;
}

const PLAY_INTERVAL_SECONDS = 5;

/** Every night of the sojourn, in order — the departure day itself sleeps nobody. */
function eachNight(start: CalendarDate, end: CalendarDate): CalendarDate[] {
  const nights: CalendarDate[] = [];
  let cursor = start;
  while (compareDates(cursor, end) < 0) {
    nights.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return nights;
}

/** Steps `current` by `delta`, wrapping past either end of a `length`-long cycle. */
function wrapIndex(current: number, delta: number, length: number): number {
  return length === 0 ? current : (current + delta + length) % length;
}

/**
 * POC: a big modal, one night at a time, showing which bed each guest sleeps
 * in that night — a first cut at a visual "scene" to react to and refine.
 */
export function DayByDayRecap({ sojourn, onClose }: DayByDayRecapProps): ReactElement {
  const t = useTranslation();
  const plan = usePlan(sojourn.id);
  const beds = useAllBeds();
  const nights = eachNight(sojourn.startDate, sojourn.endDate);
  const [dayIndex, setDayIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const currentNight = nights[dayIndex] ?? sojourn.startDate;

  // Left/right browse one night at a time, wrapping past either end so the
  // recap reads as a loop rather than a dead-ending strip.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setDayIndex((index) => wrapIndex(index, 1, nights.length));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setDayIndex((index) => wrapIndex(index, -1, nights.length));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [nights.length]);

  useEffect(() => {
    if (!playing || nights.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      setDayIndex((index) => wrapIndex(index, 1, nights.length));
    }, PLAY_INTERVAL_SECONDS * 1000);
    return () => clearInterval(id);
  }, [playing, nights.length]);

  const sojournHouseIds = new Set(sojourn.houses.map((house) => house.houseId));
  const houses = groupByHouseAndRoom(
    (beds.data ?? []).filter((bed) => sojournHouseIds.has(bed.houseId)),
  );

  const namedPlans: NamedPlan[] =
    plan.data === undefined || plan.data === null
      ? []
      : [{ sojournId: sojourn.id, sojournName: sojourn.name, plan: plan.data }];
  const selection = { start: currentNight, end: addDays(currentNight, 1) };
  const occupancy = occupantsByBed(namedPlans, selection);
  const unassigned = unassignedGuests(namedPlans, selection);

  return (
    <Dialog title={t.dayByDayRecap.titleFor(sojourn.name)} onClose={onClose} size="large">
      <div className="stack">
        <ErrorAlert error={plan.error ?? beds.error} />
        {plan.isPending || beds.isPending ? (
          <LoadingBlock label={t.dayByDayRecap.loadingRecap} />
        ) : null}

        {nights.length > 0 ? (
          <div className="stack stack--tight">
            <div className="row row--between">
              <Button
                small
                variant="ghost"
                onClick={() => setDayIndex((index) => wrapIndex(index, -1, nights.length))}
              >
                {t.dayByDayRecap.previousNight}
              </Button>
              <strong>
                {t.dayByDayRecap.nightOf(dayIndex + 1, nights.length, formatDate(currentNight))}
              </strong>
              <div className="row">
                <Button
                  small
                  variant="secondary"
                  disabled={nights.length <= 1}
                  onClick={() => setPlaying((value) => !value)}
                >
                  {playing ? t.dayByDayRecap.pause : t.dayByDayRecap.play}
                </Button>
                <Button
                  small
                  variant="ghost"
                  onClick={() => setDayIndex((index) => wrapIndex(index, 1, nights.length))}
                >
                  {t.dayByDayRecap.nextNight}
                </Button>
              </div>
            </div>
            <div className="day-recap__jump">
              {nights.map((night, index) => (
                <button
                  key={night}
                  type="button"
                  className={`day-recap__jump-day${
                    index === dayIndex ? ' day-recap__jump-day--active' : ''
                  }`}
                  aria-current={index === dayIndex}
                  aria-label={formatDate(night)}
                  onClick={() => setDayIndex(index)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {houses.length === 0 ? (
          <p className="field__hint">{t.dayByDayRecap.noHouses}</p>
        ) : namedPlans.length === 0 ? (
          <p className="field__hint">{t.dayByDayRecap.noPlan}</p>
        ) : (
          <div className="day-recap__houses">
            {houses.map((house) => (
              <div key={house.houseId} className="panel stack stack--tight">
                <h3>{house.houseName}</h3>
                <div className="day-recap__rooms">
                  {house.rooms.map((room) => (
                    <div key={room.roomId} className="day-recap__room stack stack--tight">
                      <span className="list-row__title">{room.roomName}</span>
                      <div className="stack stack--tight">
                        {room.beds.map((bed) => {
                          const occupants = occupancy.get(bed.id) ?? [];
                          return (
                            <div key={bed.id} className="row row--between">
                              <span className="card__meta" title={bed.name}>
                                {bedKindLabel(t, bed.type, bed.option)}
                              </span>
                              {occupants.length === 0 ? (
                                <Badge tone="positive">{t.dayByDayRecap.free}</Badge>
                              ) : (
                                occupants.map((occupant) => (
                                  <Badge key={occupant.guestId} tone="accent">
                                    {occupant.guestName}
                                  </Badge>
                                ))
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {unassigned.length > 0 ? (
              <div className="panel stack stack--tight">
                <h3>{t.dayByDayRecap.withoutABedTonight}</h3>
                <div className="option-list">
                  {unassigned.map((guest) => (
                    <Badge key={guest.guestId} tone="warning">
                      {guest.guestName}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Dialog>
  );
}
